import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import AddCommentIcon from "@mui/icons-material/AddComment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";

import { groupChats } from "./askModel";

const GROUPS = { today: "Today", yesterday: "Yesterday", week: "Previous 7 days", older: "Older" };

// a chat's title being edited in its row: Enter saves, Esc or leaving it cancels
function TitleInput({ chat, onSave, onCancel }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(chat.title || "");
  const box = useRef(null);
  useEffect(() => box.current.select(), []);
  return (
    <input
      ref={box}
      className="ak-sess-input"
      aria-label={t("Chat title")}
      value={title}
      maxLength={80}
      onChange={(e) => setTitle(e.target.value)}
      onBlur={onCancel}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (title.trim() && title.trim() !== chat.title) onSave(title.trim());
          else onCancel();
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

// The user's chats, grouped by day, with New Chat on top; folds into a rail of two buttons
export default function ChatList({ chats, current, open, onToggle, onRename, onDelete }) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(null); // {el, chat}
  const [editing, setEditing] = useState(null); // chat_id
  const [deleting, setDeleting] = useState(null); // chat

  if (!open) {
    return (
      <aside className="ak-side is-closed" aria-label={t("Chats")}>
        <div className="ak-side-head">
          <IconButton className="ak-side-btn" size="small" title={t("Show chats")} aria-label={t("Show chats")} onClick={onToggle}>
            <MenuIcon />
          </IconButton>
        </div>
        <IconButton className="ak-side-btn" size="small" component={Link} to="/ask" title={t("New Chat")} aria-label={t("New Chat")}>
          <AddCommentIcon />
        </IconButton>
      </aside>
    );
  }

  return (
    <aside className="ak-side" aria-label={t("Chats")}>
      <div className="ak-side-head">
        <h2 className="ak-overline">{t("Chats")}</h2>
        <IconButton className="ak-side-btn" size="small" title={t("Hide chats")} aria-label={t("Hide chats")} onClick={onToggle}>
          <MenuOpenIcon />
        </IconButton>
      </div>
      <div className={"ak-sess ak-new" + (current ? "" : " is-on")}>
        <Link to="/ask" aria-current={current ? undefined : "page"}>
          <AddCommentIcon />
          <span>{t("New Chat")}</span>
        </Link>
      </div>
      <nav className="ak-history" aria-label={t("Chat history")}>
        {groupChats(chats).map((g) => (
          <div key={g.key} className="ak-group">
            <h3 className="ak-group-title">{t(GROUPS[g.key])}</h3>
            {g.chats.map((c) => {
              const on = c.chat_id === current;
              return (
                <div key={c.chat_id} className={"ak-sess" + (on ? " is-on" : "")}>
                  {editing === c.chat_id ? (
                    <TitleInput
                      chat={c}
                      onCancel={() => setEditing(null)}
                      onSave={(title) => {
                        setEditing(null);
                        onRename(c, title);
                      }}
                    />
                  ) : (
                    <Link to={"/ask/" + c.chat_id} aria-current={on ? "page" : undefined} title={c.title}>
                      <span className="ak-sess-title">{c.title || t("New Chat")}</span>
                    </Link>
                  )}
                  {c.status === "running" ? (
                    <span className="ak-sess-busy" role="status" title={t("Answering…")} aria-label={t("Answering…")}>
                      <CircularProgress size={14} />
                    </span>
                  ) : (
                    editing !== c.chat_id && (
                      <IconButton
                        className="ak-sess-more"
                        size="small"
                        title={t("More")}
                        aria-label={t("More")}
                        aria-haspopup="true"
                        aria-expanded={menu && menu.chat === c ? "true" : undefined}
                        onClick={(e) => setMenu({ el: e.currentTarget, chat: c })}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <Menu
        anchorEl={menu && menu.el}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEditing(menu.chat.chat_id);
            setMenu(null);
          }}
        >
          {t("Rename")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleting(menu.chat);
            setMenu(null);
          }}
        >
          {t("Delete")}
        </MenuItem>
      </Menu>
      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>{t("Delete this chat?")}</DialogTitle>
        <DialogContent>{deleting && t("“{{title}}” and its answers will be deleted.", { title: deleting.title })}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>{t("Cancel")}</Button>
          <Button
            color="error"
            onClick={() => {
              onDelete(deleting);
              setDeleting(null);
            }}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </aside>
  );
}
